// Cave84 — ChangeStatusModal (Phase 1 Week 4)
// Mark a bottle as gifted / sold / lost / broken / trading (F1.4, F1.5).
// in_cellar and consumed are NOT available here (those are separate flows).

import { useState } from 'react';
import type { Bottle, BottleStatus } from '@cave84/shared-types';
import { useChangeBottleStatus } from '@/hooks/useBottles';
import { useWine } from '@/hooks/useWines';
import { useVintage } from '@/hooks/useVintages';

type ChangeableStatus = Exclude<BottleStatus, 'in_cellar' | 'consumed'>;

interface Props {
  bottle: Bottle;
  onClose: () => void;
}

const STATUS_OPTIONS: { value: ChangeableStatus; label: string; icon: string; description: string }[] = [
  {
    value: 'gifted',
    label: 'Gifted',
    icon: '🎁',
    description: 'Given as a gift',
  },
  {
    value: 'sold',
    label: 'Sold',
    icon: '💶',
    description: 'Sold at auction or privately',
  },
  {
    value: 'trading',
    label: 'Trading',
    icon: '🔄',
    description: 'Listed for trade or exchange',
  },
  {
    value: 'lost',
    label: 'Lost',
    icon: '❓',
    description: 'Misplaced or unaccounted for',
  },
  {
    value: 'broken',
    label: 'Broken / Spoiled',
    icon: '💔',
    description: 'Corked, leaking, or physically broken',
  },
];

export default function ChangeStatusModal({ bottle, onClose }: Props) {
  const [selected, setSelected] = useState<ChangeableStatus | null>(null);

  const { data: vintage } = useVintage(bottle.vintage_id);
  const { data: wine } = useWine(vintage?.wine_id);
  const changeStatus = useChangeBottleStatus();

  const label = wine && vintage ? `${vintage.year} ${wine.cuvee_name}` : 'this bottle';

  async function handleConfirm() {
    if (!selected) return;
    await changeStatus.mutateAsync({ bottle, status: selected });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-surface-1 rounded-xl border border-border-medium w-full max-w-sm p-6 shadow-xl">
        <h2 className="text-lg font-bold text-text-primary mb-1">Change Status</h2>
        <p className="text-sm text-text-secondary mb-4">
          <span className="font-medium text-text-primary">{label}</span> — select new status:
        </p>

        <div className="space-y-2 mb-5">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSelected(opt.value)}
              className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                selected === opt.value
                  ? 'border-gold bg-gold/10'
                  : 'border-border-subtle bg-surface-2 hover:bg-surface-3 hover:border-border-medium'
              }`}
            >
              <span className="text-xl mt-0.5">{opt.icon}</span>
              <div>
                <p className="text-sm font-medium text-text-primary">{opt.label}</p>
                <p className="text-xs text-text-muted">{opt.description}</p>
              </div>
            </button>
          ))}
        </div>

        {changeStatus.isError && (
          <p className="text-xs text-red-400 mb-3">{(changeStatus.error as Error).message}</p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={changeStatus.isPending}
            className="flex-1 px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={changeStatus.isPending || !selected}
            className="flex-1 px-4 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {changeStatus.isPending ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
